// ══════════════════════════════════════════════════════════════════
// CartHub Dashboard — Login
// Accesso merchant, registrazione in-app, recupero password.
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../shared";
import LanguageSelector from "./LanguageSelector";

function normalizeShopDomain(val) {
  const raw = (val || "").trim();
  if (!raw) return "";
  try {
    const withProto = raw.startsWith("http") ? raw : `https://${raw}`;
    return new URL(withProto).hostname.toLowerCase();
  } catch {
    return raw
      .replace(/^https?:\/\//i, "")
      .split("/")[0]
      .toLowerCase();
  }
}

export default function Login({ onLogin }) {
  const { t } = useTranslation("common");
  const [mode, setMode] = useState("login"); // login | register | register_done | forgot | reset
  const [email, setEmail] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [registerDoneEmail, setRegisterDoneEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  /** Dopo errore dominio: mostra box che spiega https:// vs “URL già scritto”. */
  const [registerUrlExplain, setRegisterUrlExplain] = useState(false);

  const pwType = showPw ? "text" : "password";

  const ensureShopUrlHttpsPrefix = () => {
    const v = shopDomain.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setShopDomain(`https://${v.replace(/^\/+/, "")}`);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get("reset_token");
    if (rt && rt.length >= 20) {
      setResetToken(rt);
      setMode("reset");
      window.history.replaceState({}, "", window.location.pathname || "/");
      return;
    }
    if (params.get("register") === "1") {
      setMode("register");
      window.history.replaceState({}, "", window.location.pathname || "/");
    }
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password: pw }),
      });
      if (d.token) {
        localStorage.setItem("carthub_token", d.token);
        localStorage.setItem("carthub_shop", JSON.stringify(d.shop));
        onLogin();
      } else if (d.error && d.error.includes("Troppi tentativi")) {
        setBlocked(true);
        setErr(d.error);
        setTimeout(() => {
          setBlocked(false);
          setErr("");
        }, 15 * 60 * 1000);
      } else setErr(d.error || t("login.errors.invalidCredentials"));
    } catch {
      setErr(t("login.errors.connection"));
    }
    setLoading(false);
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    setRegisterUrlExplain(false);
    if (!email.trim() || !shopDomain.trim() || !pw || !pw2) {
      setErr(t("login.errors.registerFields"));
      return;
    }
    if (pw.length < 8) {
      setErr(t("login.errors.passwordMin"));
      return;
    }
    if (pw !== pw2) {
      setErr(t("login.errors.passwordMismatch"));
      return;
    }
    let rawShop = shopDomain.trim();
    if (rawShop && !/^https?:\/\//i.test(rawShop)) {
      rawShop = `https://${rawShop.replace(/^\/+/, "")}`;
      setShopDomain(rawShop);
    }
    const shop_domain = normalizeShopDomain(rawShop);
    if (!shop_domain || !shop_domain.includes(".")) {
      setErr(t("login.errors.registerDomain"));
      setRegisterUrlExplain(true);
      return;
    }
    setLoading(true);
    try {
      const d = await api("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          shop_domain,
          password: pw,
        }),
      });
      if (d.success) {
        setRegisterDoneEmail(email.trim());
        setEmail("");
        setShopDomain("");
        setPw("");
        setPw2("");
        setShowPw(false);
        setMode("register_done");
      } else {
        setErr(d.error || t("login.errors.registerFailed"));
      }
    } catch {
      setErr(t("login.errors.connection"));
    }
    setLoading(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      if (d.error) {
        setErr(d.error);
      } else {
        setOk(d.message || t("login.errors.checkEmail"));
        setEmail("");
      }
    } catch {
      setErr(t("login.errors.connection"));
    }
    setLoading(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      setErr(t("login.errors.passwordMin"));
      return;
    }
    if (pw !== pw2) {
      setErr(t("login.errors.passwordMismatch"));
      return;
    }
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: resetToken, password: pw }),
      });
      if (d.error) {
        setErr(d.error);
      } else {
        setOk(d.message || t("login.errors.passwordUpdated"));
        setPw("");
        setPw2("");
        setResetToken("");
        setMode("login");
      }
    } catch {
      setErr(t("login.errors.connection"));
    }
    setLoading(false);
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo" aria-label="CartHub">
          Cart<span>Hub</span>
        </div>
        <div className="login-sub">{t("login.subtitle")}</div>

        {mode === "login" && (
          <form onSubmit={submitLogin}>
            <label className="login-label">{t("login.email")}</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.placeholderEmail")}
              autoComplete="username"
              required
            />
            <label className="login-label">{t("login.password")}</label>
            <input
              className="login-input"
              type={pwType}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
            <div className="login-checkbox-row">
              <input
                type="checkbox"
                id="login-show-pw"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />
              <label htmlFor="login-show-pw">{t("login.showPassword")}</label>
            </div>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => {
                setMode("forgot");
                setErr("");
                setOk("");
              }}
            >
              {t("login.forgotPassword")}
            </button>
            <button
              className="login-btn"
              type="submit"
              disabled={loading || blocked}
            >
              {loading
                ? t("login.submitLoading")
                : blocked
                  ? t("login.submitBlocked")
                  : t("login.submit")}
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
            <div className="login-footer">
              <span className="login-footer-muted">{t("login.footerNoAccount")}</span>
              <button
                type="button"
                className="login-footer-link"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  font: "inherit",
                }}
                onClick={() => {
                  setMode("register");
                  setErr("");
                  setOk("");
                  setPw("");
                  setShowPw(false);
                  setRegisterUrlExplain(false);
                }}
              >
                {t("login.footerSignup")}
              </button>
            </div>
          </form>
        )}

        {mode === "register" && (
          <form onSubmit={submitRegister}>
            <p className="login-lead">{t("login.registerSub")}</p>
            <label className="login-label">{t("login.email")}</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.placeholderEmail")}
              autoComplete="email"
              required
            />
            <label className="login-label">{t("login.shopUrl")}</label>
            <input
              className="login-input"
              type="text"
              inputMode="url"
              value={shopDomain}
              onChange={(e) => {
                setRegisterUrlExplain(false);
                setShopDomain(e.target.value);
              }}
              onBlur={ensureShopUrlHttpsPrefix}
              placeholder={t("login.shopUrlPlaceholder")}
              autoComplete="url"
              required
            />
            <p className="login-lead" style={{ fontSize: 12, color: "#6b7280", marginTop: -8 }}>
              {t("login.shopUrlHint")}
            </p>
            <label className="login-label">{t("login.password")}</label>
            <input
              className="login-input"
              type={pwType}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={t("login.placeholderMin8")}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <label className="login-label">{t("login.confirmPassword")}</label>
            <input
              className="login-input"
              type={pwType}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder={t("login.placeholderRepeat")}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <div className="login-checkbox-row">
              <input
                type="checkbox"
                id="reg-show-pw"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />
              <label htmlFor="reg-show-pw">{t("login.showPassword")}</label>
            </div>
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? t("login.registerSubmitLoading") : t("login.registerSubmit")}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => {
                setMode("login");
                setErr("");
                setOk("");
                setShopDomain("");
                setPw("");
                setPw2("");
                setShowPw(false);
                setRegisterUrlExplain(false);
              }}
            >
              {t("login.backToLogin")}
            </button>
            {err && <div className="login-err">{err}</div>}
            {registerUrlExplain && (
              <div className="login-url-hint-bubble" role="note">
                {t("login.errors.registerDomainHint")}
              </div>
            )}
          </form>
        )}

        {mode === "register_done" && (
          <div>
            <p className="login-lead" style={{ textAlign: "center", fontWeight: 600 }}>
              {t("login.registerSuccessTitle")}
            </p>
            <p className="login-lead" style={{ textAlign: "center" }}>
              {t("login.registerSuccessBody", { email: registerDoneEmail })}
            </p>
            <button
              type="button"
              className="login-btn"
              onClick={() => {
                setMode("login");
                setRegisterDoneEmail("");
                setErr("");
              }}
            >
              {t("login.registerBack")}
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot}>
            <p className="login-lead">{t("login.forgotLead")}</p>
            <label className="login-label">{t("login.email")}</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.placeholderEmail")}
              autoComplete="email"
              required
            />
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? t("login.sendLoading") : t("login.sendInstructions")}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => {
                setMode("login");
                setErr("");
                setOk("");
              }}
            >
              {t("login.backToLogin")}
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={submitReset}>
            <p className="login-lead">{t("login.resetLead")}</p>
            <label className="login-label">{t("login.newPassword")}</label>
            <input
              className="login-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={t("login.placeholderMin8")}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <label className="login-label">{t("login.confirmPassword")}</label>
            <input
              className="login-input"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder={t("login.placeholderRepeat")}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? t("login.saveLoading") : t("login.savePassword")}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => {
                setMode("login");
                setResetToken("");
                setErr("");
                setOk("");
                setPw("");
                setPw2("");
              }}
            >
              {t("login.backToLogin")}
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
          </form>
        )}

        <div className="login-lang-footer">
          <LanguageSelector variant="login" />
        </div>
      </div>
    </div>
  );
}
